export interface RetryOptions {
  retries?: number;
  backoff?: number;
  factor?: number;
  onRetry?: (attempt: number, delay: number, error: Error) => void;
  /**
   * H-305: predicate to gate retries. Return `true` to retry the operation, `false`
   * to throw immediately. Default: retry on any thrown error (backwards-compat).
   *
   * Use this for operations where some errors are transient (network failures, rate
   * limits) but others are logical and won't change on retry (validation errors,
   * already-exists). Without this gate, retrying a logical error wastes the entire
   * backoff window before propagating the unchanged failure.
   */
  shouldRetry?: (error: Error) => boolean;
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options?: RetryOptions
): Promise<T> {
  const { retries = 3, backoff = 500, factor = 2, onRetry, shouldRetry } = options ?? {};
  let attempt = 0;
  while (attempt <= retries) {
    try {
      return await operation();
    } catch (error: unknown) {
      const e = error instanceof Error ? error : new Error(String(error));
      // H-305: skip the retry loop entirely for non-retryable errors so the caller
      // sees the logical failure immediately instead of after backoff×retries ms.
      if (shouldRetry && !shouldRetry(e)) {
        throw e;
      }
      attempt++;
      if (attempt > retries) {
        throw e;
      }

      const delay = backoff * Math.pow(factor, attempt - 1);
      if (onRetry) {
        onRetry(attempt, delay, e);
      } else {
        console.warn(
          `[Retry] Operation failed (Attempt ${attempt}/${retries}). Retrying in ${delay}ms... Error: ${e.message}`
        );
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error('Unreachable');
}
